import * as eff from 'redux-saga/effects';
import {
  channel,
} from 'redux-saga';
import {
  toast,
} from 'react-toastify';
import i18n from 'i18next';
import MobileDetect from 'mobile-detect';
import mixpanel from 'mixpanel-browser';

import config from 'web-config';
import {
  api,
  coreSagas,
  coreActions,
} from '@gymcloud/core';
import {
  history,
} from 'web-history';
import {
  urls,
} from 'web-urls';
import {
  fixVHUnit,
  getNamespaceSymbol,
} from 'web-utils';

import {
  uiActions,
} from 'web-actions';

import {
  initialApi,
  initialRedirect,
  analyticsIdentify,
} from './utils';

import cableFlow from './cable';

const md = new MobileDetect(window.navigator.userAgent);
const namespaceSymbol = getNamespaceSymbol() || 'gymcloud';

history.listen(({ pathname }) => {
  // /workout_templates/198814/workout => Workout Templates
  const name = pathname.split('/')[1]
    .replace(/\?(.*)$/, '')
    .replace(/\d+/, '')
    .replace('folder', '')
    .replace(/_/g, ' ')
    .replace(/\//g, '_')
    .split(' ')
    .filter(str => !!str)
    .map(str => `${str[0].toUpperCase()}${str.substr(1)}`)
    .join(' ');

  mixpanel.track(
    `Viewed ${name}`,
    {
      name,
      path: pathname,
      'User Namespace': namespaceSymbol,
    },
  );
});

// CSS3 100vh not constant in mobile browser
fixVHUnit();

function* redirectToApp(message: {
  user: {
    namespace: string,
  },
  accessToken: string,
}): Generator<any, void, any> {
  try {
    const namespace = yield eff.call(
      api.getNamespace,
      {
        symbol: message.user.namespace,
      },
    );

    if (namespace.app_url) {
      const url = namespace.app_url.replace(/\/$/, '');
      window.location.href = `${url}/accessToken/${message.accessToken}`;
    } else {
      const { host } = window.location;
      window.location.href = `https://${namespace.symbol}.${host}/accessToken/${message.accessToken}`;
    }
  } catch (e) {
    console.log(e);
  }
}

function* coreFlows(webChannel): Generator<any, void, any> {
  const effects = [
    coreSagas.announcementFlow,
    coreSagas.authFlow,
    coreSagas.entityFlow,
    coreSagas.productsFlow,
    coreSagas.paymentsFlow,
    coreSagas.documentFlow,
    coreSagas.videoFlow,
    coreSagas.groupFlow,
    coreSagas.headerFlow,
    coreSagas.clientFlow,
    coreSagas.workoutFlow,
    coreSagas.programFlow,
    coreSagas.exerciseFlow,
    coreSagas.conversationFlow,
    coreSagas.workoutEventFlow,
    coreSagas.personalWorkoutFlow,
  ];

  yield eff.all(
    effects.map(
      effect => eff.fork(effect, webChannel),
    ),
  );
}

function* initializeNamespace() {
  try {
    const namespace = yield eff.call(
      api.getNamespace,
      {
        symbol: namespaceSymbol,
      },
    );

    i18n.setDefaultNamespace(namespace.symbol);

    yield eff.put(
      uiActions.setUiState({
        namespace,
        namespaceIsLoaded: true,
      }),
    );
  } catch (e) {
    console.log(e);
  }
}

export default function* initialize(): Generator<any, void, any> {
  initialApi();
  const webChannel = yield eff.call(channel);
  yield eff.fork(
    initializeNamespace,
  );
  yield eff.fork(
    coreFlows,
    webChannel,
  );

  if (config.mixpanel.token) {
    mixpanel.init(
      config.mixpanel.token,
      {
        debug: process.env.TARGET_ENV !== 'production',
        persistence: 'localStorage',
      },
    );
  }

  const userId = yield eff.call([localStorage, 'getItem'], 'userId');
  const accessToken = yield eff.call([localStorage, 'getItem'], 'accessToken');
  const hasToken = !!(userId && accessToken);
  if (hasToken) {
    yield eff.put(
      webChannel,
      {
        messageType: 'accessTokenExist',
        message: {
          userId,
          accessToken,
        },
      },
    );
  }

  yield eff.put(
    coreActions.setProfileState({
      hasToken,
    }),
  );

  initialRedirect(hasToken);

  let cableTask;
  let profileTask;
  let initializeTask;
  while (true) {
    const {
      messageType,
      message,
    } = yield eff.take(webChannel);

    switch (messageType) {
      case 'accessTokenExist': {
        api.setAccessToken(message.accessToken);
        api.setAdditionalParams({ userId: message.userId });

        cableTask = yield eff.fork(
          cableFlow,
          message,
        );

        profileTask = yield eff.fork(
          coreSagas.profileFlow,
          webChannel,
        );
        break;
      }
      case 'loginSuccess': {
        if (md.mobile()) {
          history.push(urls.getApp);
          break;
        }

        if (window.location.host === 'app.gymcloud.com'
            && !message.user.namespace.startsWith('namespace_')
            && message.user.namespace !== 'gymcloud') {
          yield redirectToApp(message);
          break;
        }

        cableTask = yield eff.fork(
          cableFlow,
          {
            userId: message.user.id,
            accessToken: message.accessToken,
          },
        );

        profileTask = yield eff.fork(
          coreSagas.profileFlow,
          webChannel,
        );

        yield eff.put(
          coreActions.setProfileState({
            hasToken: true,
          }),
        );

        localStorage.setItem(
          'userId',
          message.user.id,
        );
        localStorage.setItem(
          'accessToken',
          message.accessToken,
        );

        if (!message.payment_required) {
          history.push(message.redirectTo || urls.dashboardPage);
        }
        break;
      }
      case 'fetchProfileSuccess': {
        if (initializeTask) {
          yield eff.cancel(initializeTask);
        }
        initializeTask = yield eff.fork(
          message.is_pro
            ? coreSagas.initialProResources
            : coreSagas.initialClientResources,
          webChannel,
        );

        analyticsIdentify(message);
        break;
      }
      case 'reloadProfile': {
        if (profileTask) {
          yield eff.cancel(profileTask);
        }

        profileTask = yield eff.fork(
          coreSagas.profileFlow,
          webChannel,
        );
        break;
      }
      case 'notify': {
        const fnToast = (message.type && message.type !== 'dismiss' ? toast[message.type] : toast);
        if (message.type === 'dismiss') {
          fnToast.dismiss();
        } else if (message.body) {
          fnToast(message.body, message.settings);
        } else if (message.key) {
          const body = i18n.t(message.key, message.params);
          fnToast(body || '', message.settings);
        }
        break;
      }
      case 'logoutRequest': {
        history.push(urls.login);

        api.setAccessToken(null);
        api.clearAdditionalParams();

        localStorage.removeItem('userId');
        localStorage.removeItem('accessToken');
        mixpanel.reset();

        if (cableTask) {
          yield eff.cancel(cableTask);
        }

        if (profileTask) {
          yield eff.cancel(profileTask);
        }

        yield eff.put({
          type: 'LOG_OUT',
        });
        break;
      }
      default:
        break;
    }
  }
}
